type CountriesResponse = {
  countries: Country[]
}

import { HttpClient } from '@angular/common/http'
import { DestroyRef, inject, Injectable, signal } from '@angular/core'
import { Observable, tap } from 'rxjs'
import { Response } from '../types/generic-response.type'
import { environment } from '@environments/environment'
import { Country } from '../types/country.type'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { IndustrySector } from '../types/industry-sector.type'
import { IndustrySectorsResponse } from './industry-sector.service'

type DetectedLocation = { country: string; country_code: string }

@Injectable({
  providedIn: 'root'
})
export class AppService {
  private _Http = inject(HttpClient)
  private _industrySectors = signal<IndustrySector[]>([])
  private _countries = signal<Country[]>([])
  //   private _currencies!: Currency[];
  public detectedCountry = signal<Country | null>(null)
  private _detectedLocation = signal<DetectedLocation | null>(null)
  private _destroyRef = inject(DestroyRef)
  // private _selectedDomain$ = new BehaviorSubject<null>(null);

  //   public set selectedDomain(domain: Domain | null) {
  //     if(domain) localStorage.setItem('domain', domain as string);
  //     this._selectedDomain$.next(domain);
  //   }

  set industrySectors(industrySectors: IndustrySector[]) {
    this._industrySectors.set(industrySectors)
  }

  set countries(countries: Country[]) {
    localStorage.setItem('countries', JSON.stringify(countries))
    this._countries.set(countries)
  }

  //   set currencies(currencies: Currency[]) {
  //     localStorage.setItem('currencies', JSON.stringify(currencies));
  //     this._currencies = currencies;
  //   }

  get industrySectors(): IndustrySector[] {
    return this._industrySectors()
  }

  get countries(): Country[] {
    return JSON.parse(localStorage.getItem('countries') as string) || this._countries()
  }

  /**
   * Cached detected user country (used by phone/country inputs).
   * Components should not read localStorage directly.
   */
  public getCachedUserCountry(): Country | null {
    try {
      const raw = localStorage.getItem('userCountry')
      return raw ? (JSON.parse(raw) as Country) : null
    } catch {
      return null
    }
  }

  //   get currencies(): Currency[] {
  //     return JSON.parse(
  //       localStorage.getItem('currencies') as string
  //     ) || this._currencies;
  //   }

  //   navbarChanges$: Subject<Partial<NavBarUpdates>> = new Subject();

  constructor(private _http: HttpClient) {}

  //   public selectedDomain$(): Observable<Domain> {
  //     if (!this._selectedDomain$.value)
  //       this.selectedDomain =
  //         (localStorage.getItem('domain') as Domain | null) ??
  //         (this._Router.url.split('/')[1] as Domain | null);
  //     return this._selectedDomain$ as Observable<Domain>;
  //   }

  /**
   * @description set the detected country from the countries array
   * @param iso2 string
   * @returns Country | undefined
   */
  public setTheDetectedLocation(iso2?: string): Country | undefined {
    return this.countries?.find((country) => country.iso2 === iso2 || country.iso2 === this._detectedLocation()?.country_code)
  }

  /**
   * @description Detects the user's IP address dynamically
   * @returns Promise<string> - The detected IP address
   */
  private async _detectUserIP(): Promise<string> {
    const response = await fetch('https://api.ipify.org?format=json')
    const data = await response.json()
    return data.ip
  }

  /**
   * @description Gets the detected user location from IP address
   * @param ip - IP address to check
   * @returns Response<DetectedLocation>
   */
  public getDetectedLocation(ip: string): Observable<Response<DetectedLocation>> {
    return this._http.get<Response<DetectedLocation>>(`${environment.v2.baseUrl}generic/ip-to-country?ip=${ip}`).pipe(
      tap((response) => {
        this._detectedLocation.set(response.data)
        this.detectedCountry.set(this.setTheDetectedLocation())
        if (this.detectedCountry()) localStorage.setItem('userCountry', JSON.stringify(this.detectedCountry()))
      })
    )
  }

  /**
   * @description Gets the detected user location automatically (detects IP first)
   * @returns Observable<Response<DetectedLocation>>
   */
  public getDetectedLocationAuto(): Observable<Response<DetectedLocation>> {
    return new Observable((observer) => {
      this._detectUserIP()
        .then((ip) => {
          this.getDetectedLocation(ip)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
              next: (response) => observer.next(response),
              error: (error) => observer.error(error),
              complete: () => observer.complete()
            })
        })
        .catch((error) => observer.error(error))
    })
  }

  /**
   * @description Gets All countries Objects
   * @returns Response<{ countries: Country[] }>
   */
  public getAllCountries(): Observable<Response<CountriesResponse>> {
    return this._Http.get<Response<CountriesResponse>>(`${environment.v2.baseUrl}generic/countries`).pipe(
      tap((response) => {
        this.countries = response.data.countries
        this.getDetectedLocationAuto()
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe({
            next: () => {},
            error: () => {}
          })
      })
    )
  }

  //   /**
  //    * @description Get all currencies
  //    * @returns Observable<Response<{ currencies: Currency[] }>>
  //    */
  //   public getAllCurrencies(): Observable<Response<{ currencies: Currency[] }>>{
  //     return this._Http.get<Response<{ currencies: Currency[] }>>(
  //       `${environment.genericApi}currencies`
  //     )
  //   }

  /**
   * @method setIndustrySectors
   * @description Method to set industry sectors in the subject
   * @returns Response<IndustrySector[]>
   */
  public getIndustrySectorList(): Observable<Response<IndustrySectorsResponse>> {
    return this._Http.get<Response<IndustrySectorsResponse>>(`${environment.v2.baseUrl}generic/industry-sectors`)
  }

  //   public updateNavbar(changes: Partial<NavBarUpdates>) {
  //     this.navbarChanges$.next(changes);
  //   }

  /**
   * objectToFormData
   * @description Creates a formData from an object
   * @param obj - the object to create the formData from
   * @returns formData
   * */
  objectToFormData(obj: { [key: string]: any }): FormData {
    const formData = new FormData()
    this._appendFormData(formData, obj, '')
    return formData
  }

  /**
   * _appendFormData
   * @description Appends data from object into a formData
   * @returns void
   * */
  private _appendFormData(formData: FormData, obj: { [key: string]: any }, keyPrefix: string = ''): void {
    for (const key in obj) {
      const newKey = keyPrefix ? `${keyPrefix}[${key}]` : key
      const value = obj[key]
      if (value === undefined) continue
      if (Array.isArray(value)) {
        value.forEach((item: any, index: number) => {
          if (item instanceof File || typeof item === 'string') {
            formData.append(`${newKey}[${index}]`, item)
            return
          }
          if (typeof item !== 'string') this._appendFormData(formData, item, `${newKey}[${index}]`)
        })
      } else if (typeof value === 'object' && value !== null && !(value instanceof File)) {
        this._appendFormData(formData, value, newKey)
      } else {
        formData.append(newKey, value)
      }
    }
  }

  /**
   * @method checkForEmailDomain
   * @description Method to check if email domain is valid or not
   * @param Observable<Response<{ valid_domain: boolean }>>
   */
  public checkForEmailDomain(email: string) {
    return this._Http.post<Response<{ valid_domain: boolean }>>(`${environment.v2.baseUrl}auth/verify-domain`, {
      email
    })
  }

  /**
   * @method checkForEventShortName
   * @description Method to check if event short name exists
   * @param organization number
   * @param shortName string
   * @param eventId number
   * @returns Observable<Response<{ short_name_exists: boolean }>>
   * */
  //   public checkForEventShortName(
  //     organization: number,
  //     shortName: string,
  //     eventId?: number
  //   ): Observable<Response<{ short_name_exists: boolean }>> {
  //     return this._Http.get<Response<{ short_name_exists: boolean }>>(
  //       `${environment.planApi
  //       }${organization}/events/short-name-exists?short_name=${shortName}${eventId ? `&event_id=${eventId}` : ''
  //       }`
  //     );
  //   }

  /**
   *@description  Gets the user country from localStorage
   * @returns Country | null
   */
  public getUserCountry(): Country | null {
    const userCountry = localStorage.getItem('userCountry')
    return userCountry ? JSON.parse(userCountry) : null
  }

  /**
   * @description Checks if countries are loaded
   * @returns boolean
   */
  public areCountriesLoaded(): boolean {
    return this.countries && this.countries.length > 0
  }

  /**
   * @description Gets the country code from the search term either if it's the iso2 or the name
   * @param searchTerm string
   * @returns string | null
   */
  public getCountryCode(searchTerm: string): string {
    const lowerCasedTerm = searchTerm?.toLowerCase()

    for (const country of this.countries) {
      // Check for a match with iso2
      if (country.iso2.toLowerCase() === lowerCasedTerm) return country.country_code

      // Check for a match with name
      if (country.name.toLowerCase() === lowerCasedTerm) return country.country_code

      // Pre-process the alternatives only once per country
      const alternatives = country.alternatives.split(',').map((alt) => alt.toLowerCase().trim())

      // Check for a match with alternatives
      if (alternatives.includes(lowerCasedTerm)) return country.country_code
    }

    return ''
  }

  //   /**
  //    * @description Method to load the google maps script
  //    */
  //   loadGoogleMaps(): void {
  //     if (document.getElementById('google-maps-script')) return;
  //     const script = document.createElement('script');
  //     script.src = `https://maps.googleapis.com/maps/api/js?libraries=places&key=${environment.GOOGLE_MAPS_API_KEY}`;
  //     script.defer = true;
  //     script.async = true;
  //     script.id = 'google-maps-script';
  //     document.head.appendChild(script);
  //   }

  /**
   * @description Global function to download a blob
   * @param blob Blob | null
   * @param fileName string | null
   */
  public downloadBlob(blob: Blob | null, fileName: string | null): void {
    if (!blob || !fileName) return
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.click()
    // Revoking synchronously can cancel the download in Safari/Firefox.
    setTimeout(() => window.URL.revokeObjectURL(url), 1000)
  }
}
